<template>
  <div class="role-management">
    <!-- 页面标题 -->
    <div class="page-header mb-4">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <h1 class="page-title">
            <i class="bi bi-shield-check me-2"></i>
            角色管理
          </h1>
          <p class="page-subtitle text-muted mb-0">
            管理系统中的角色和权限配置
          </p>
        </div>
        <div class="header-actions">
          <button class="btn btn-primary" @click="showCreateModal = true">
            <i class="bi bi-plus-lg me-1"></i>
            创建角色
          </button>
        </div>
      </div>
    </div>

    <!-- 搜索和筛选 -->
    <div class="search-section mb-4">
      <div class="row">
        <div class="col-md-6">
          <div class="input-group">
            <span class="input-group-text">
              <i class="bi bi-search"></i>
            </span>
            <input
              type="text"
              class="form-control"
              placeholder="搜索角色名称或描述..."
              v-model="searchQuery"
              @input="handleSearch"
            />
          </div>
        </div>
        <div class="col-md-3">
          <select class="form-select" v-model="statusFilter" @change="handleFilter">
            <option value="">所有状态</option>
            <option value="active">启用</option>
            <option value="inactive">禁用</option>
          </select>
        </div>
      </div>
    </div>

    <!-- 角色列表 -->
    <div class="roles-grid">
      <div class="row">
        <div class="col-lg-4 col-md-6 mb-4" v-for="role in filteredRoles" :key="role.id">
          <div class="role-card">
            <div class="role-header">
              <div class="role-icon">
                <i :class="getRoleIcon(role.name)"></i>
              </div>
              <div class="role-info">
                <h5 class="role-name">{{ role.displayName }}</h5>
                <p class="role-description">{{ role.description }}</p>
              </div>
              <div class="role-status">
                <span class="badge" :class="role.isActive ? 'bg-success' : 'bg-danger'">
                  {{ role.isActive ? '启用' : '禁用' }}
                </span>
              </div>
            </div>
            
            <div class="role-stats">
              <div class="stat-item">
                <span class="stat-number">{{ role.userCount || 0 }}</span>
                <span class="stat-label">用户数</span>
              </div>
              <div class="stat-item">
                <span class="stat-number">{{ role.permissions?.length || 0 }}</span>
                <span class="stat-label">权限数</span>
              </div>
            </div>
            
            <div class="role-permissions">
              <h6 class="permissions-title">权限列表</h6>
              <div class="permissions-list">
                <span 
                  v-for="permission in role.permissions?.slice(0, 6)" 
                  :key="permission"
                  class="permission-tag"
                >
                  {{ getPermissionDisplayName(permission) }}
                </span>
                <span v-if="role.permissions && role.permissions.length > 6" class="permission-more">
                  +{{ role.permissions.length - 6 }} 更多
                </span>
              </div>
            </div>
            
            <div class="role-actions">
              <button class="btn btn-outline-primary btn-sm" @click="editRole(role)">
                <i class="bi bi-pencil me-1"></i>
                编辑
              </button>
              <button 
                class="btn btn-sm" 
                :class="role.isActive ? 'btn-outline-warning' : 'btn-outline-success'"
                @click="toggleRoleStatus(role)"
              >
                <i :class="role.isActive ? 'bi bi-pause' : 'bi bi-play'" class="me-1"></i>
                {{ role.isActive ? '禁用' : '启用' }}
              </button>
              <button 
                class="btn btn-outline-danger btn-sm" 
                @click="deleteRole(role)"
                :disabled="role.name === 'admin' || role.name === 'super_admin' || role.name === 'user'"
              >
                <i class="bi bi-trash me-1"></i>
                删除
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 空状态 -->
      <div v-if="filteredRoles.length === 0" class="text-center py-5">
        <i class="bi bi-shield-x display-1 text-muted mb-3"></i>
        <h5 class="text-muted">暂无角色数据</h5>
        <p class="text-muted">{{ searchQuery ? '没有找到匹配的角色' : '系统中还没有角色' }}</p>
      </div>
    </div>

    <!-- 创建/编辑角色模态框 -->
    <div class="modal fade" :class="{ show: showCreateModal || showEditModal }" :style="{ display: showCreateModal || showEditModal ? 'block' : 'none' }" tabindex="-1">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="bi bi-shield-plus me-2"></i>
              {{ showCreateModal ? '创建角色' : '编辑角色' }}
            </h5>
            <button type="button" class="btn-close" @click="closeModal"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="saveRole">
              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="form-label">角色名称 <span class="text-danger">*</span></label>
                  <input 
                    type="text" 
                    class="form-control" 
                    v-model="roleForm.name" 
                    :disabled="showEditModal"
                    required
                  >
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-label">显示名称 <span class="text-danger">*</span></label>
                  <input type="text" class="form-control" v-model="roleForm.displayName" required>
                </div>
                <div class="col-12 mb-3">
                  <label class="form-label">描述</label>
                  <textarea class="form-control" rows="3" v-model="roleForm.description" placeholder="角色描述信息"></textarea>
                </div>
                <div class="col-12 mb-3">
                  <label class="form-label">权限配置 <span class="text-danger">*</span></label>
                  <div class="permissions-config">
                    <div class="row">
                      <div class="col-md-6 mb-2" v-for="permission in availablePermissions" :key="permission.key">
                        <div class="form-check">
                          <input 
                            class="form-check-input" 
                            type="checkbox" 
                            :id="permission.key"
                            :value="permission.key"
                            v-model="roleForm.permissions"
                          >
                          <label class="form-check-label" :for="permission.key">
                            <strong>{{ permission.name }}</strong>
                            <small class="text-muted d-block">{{ permission.description }}</small>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-label">状态</label>
                  <div class="form-check form-switch">
                    <input class="form-check-input" type="checkbox" v-model="roleForm.isActive">
                    <label class="form-check-label">
                      {{ roleForm.isActive ? '启用' : '禁用' }}
                    </label>
                  </div>
                </div>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeModal">取消</button>
            <button type="button" class="btn btn-primary" @click="saveRole" :disabled="saving">
              <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
              {{ showCreateModal ? '创建' : '保存' }}
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 模态框背景 -->
    <div class="modal-backdrop fade" :class="{ show: showCreateModal || showEditModal }" v-if="showCreateModal || showEditModal"></div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useAuthStore } from '../../stores/auth'
import { usersApi } from '../../api/users'

interface Role {
  id: number
  name: string
  displayName: string
  description: string
  permissions: string[]
  isActive: boolean
  userCount?: number
  createdAt: Date
}

interface RoleForm {
  name: string
  displayName: string
  description: string
  permissions: string[]
  isActive: boolean
}

interface Permission {
  key: string
  name: string
  description: string
}

const authStore = useAuthStore()

// 响应式数据
const roles = ref<Role[]>([])
const searchQuery = ref('')
const statusFilter = ref('')
const loading = ref(false)
const saving = ref(false)

// 模态框状态
const showCreateModal = ref(false)
const showEditModal = ref(false)
const editingRole = ref<Role | null>(null)

// 角色表单
const roleForm = ref<RoleForm>({
  name: '',
  displayName: '',
  description: '',
  permissions: [],
  isActive: true
})

// 可用权限列表
const availablePermissions: Permission[] = [
  { key: 'user.view', name: '查看用户', description: '查看用户列表和详情' },
  { key: 'user.create', name: '创建用户', description: '创建新用户账户' },
  { key: 'user.edit', name: '编辑用户', description: '修改用户信息' },
  { key: 'user.delete', name: '删除用户', description: '删除用户账户' },
  { key: 'role.view', name: '查看角色', description: '查看角色列表和权限' },
  { key: 'role.create', name: '创建角色', description: '创建新角色' },
  { key: 'role.edit', name: '编辑角色', description: '修改角色权限' },
  { key: 'role.delete', name: '删除角色', description: '删除角色' },
  { key: 'question.view', name: '查看题库', description: '查看题库和题目' },
  { key: 'question.create', name: '创建题库', description: '创建新题库和题目' },
  { key: 'question.edit', name: '编辑题库', description: '修改题库和题目' },
  { key: 'question.delete', name: '删除题库', description: '删除题库和题目' },
  { key: 'system.config', name: '系统配置', description: '修改系统配置' },
  { key: 'system.log', name: '系统日志', description: '查看系统日志' }
]

// 计算属性
const filteredRoles = computed(() => {
  let filtered = roles.value
  
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    filtered = filtered.filter(role => 
      role.name.toLowerCase().includes(query) ||
      role.displayName.toLowerCase().includes(query) ||
      role.description.toLowerCase().includes(query)
    )
  }
  
  if (statusFilter.value) {
    const isActive = statusFilter.value === 'active'
    filtered = filtered.filter(role => role.isActive === isActive)
  }
  
  return filtered
})

// 方法
const loadRoles = async () => {
  loading.value = true
  try {
    const rolesData = await usersApi.getRoles()
    roles.value = rolesData.map(role => ({
      id: role.id,
      name: role.name,
      displayName: role.name,
      description: role.description || '',
      permissions: role.permissions || [],
      isActive: true, // 假设所有角色都是活跃的
      userCount: 0, // 暂时设为0，可以后续通过其他API获取
      createdAt: new Date(role.createdAt)
    }))
  } catch (error) {
    console.error('加载角色列表失败:', error)
  } finally {
    loading.value = false
  }
}

const handleSearch = () => {
  // 搜索逻辑已在计算属性中处理
}

const handleFilter = () => {
  // 筛选逻辑已在计算属性中处理
}

const getRoleIcon = (roleName: string) => {
  switch (roleName) {
    case 'super_admin':
      return 'bi bi-shield-fill-check text-danger'
    case 'admin':
      return 'bi bi-shield-check text-warning'
    case 'user':
      return 'bi bi-person-circle text-primary'
    default:
      return 'bi bi-shield text-secondary'
  }
}

const getPermissionDisplayName = (permission: string) => {
  const found = availablePermissions.find(p => p.key === permission)
  return found ? found.name : permission
}

const editRole = (role: Role) => {
  editingRole.value = role
  roleForm.value = {
    name: role.name,
    displayName: role.displayName,
    description: role.description,
    permissions: [...role.permissions],
    isActive: role.isActive
  }
  showEditModal.value = true
}

const toggleRoleStatus = async (role: Role) => {
  try {
    // 模拟API调用
    await new Promise(resolve => setTimeout(resolve, 300))
    role.isActive = !role.isActive
  } catch (error) {
    console.error('切换角色状态失败:', error)
  }
}

const deleteRole = async (role: Role) => {
  if (confirm(`确定要删除角色 "${role.displayName}" 吗？此操作不可撤销。`)) {
    try {
      await usersApi.deleteRole(role.id)
      await loadRoles() // 重新加载角色列表
    } catch (error) {
      console.error('删除角色失败:', error)
    }
  }
}

const saveRole = async () => {
  if (roleForm.value.permissions.length === 0) {
    alert('请至少选择一个权限')
    return
  }
  
  saving.value = true
  try {
    if (showCreateModal.value) {
      // 创建新角色
      const createData = {
        name: roleForm.value.name,
        description: roleForm.value.description,
        permissions: roleForm.value.permissions
      }
      await usersApi.createRole(createData)
      await loadRoles() // 重新加载角色列表
    } else {
      // 更新角色
      if (editingRole.value) {
        const updateData = {
          name: roleForm.value.name,
          description: roleForm.value.description,
          permissions: roleForm.value.permissions
        }
        await usersApi.updateRole(editingRole.value.id, updateData)
        await loadRoles() // 重新加载角色列表
      }
    }
    
    closeModal()
  } catch (error) {
    console.error('保存角色失败:', error)
  } finally {
    saving.value = false
  }
}

const closeModal = () => {
  showCreateModal.value = false
  showEditModal.value = false
  editingRole.value = null
  roleForm.value = {
    name: '',
    displayName: '',
    description: '',
    permissions: [],
    isActive: true
  }
}

onMounted(() => {
  loadRoles()
})
</script>

<style scoped>
.role-management {
  padding: 1.5rem;
  background-color: #F0F9FF;
  min-height: 100vh;
}

.page-header {
  background: white;
  color: #0C4A6E;
  padding: 1.5rem 2rem;
  border-radius: 12px;
  margin-bottom: 1.5rem !important;
  box-shadow: 0 4px 6px -1px rgba(14, 165, 233, 0.1), 0 2px 4px -1px rgba(14, 165, 233, 0.06);
  border-left: 4px solid #0EA5E9;
}

.page-title {
  font-size: 1.75rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
  color: #0C4A6E;
}

.page-subtitle {
  font-size: 0.95rem;
  color: #475569 !important;
}

.header-actions .btn {
  font-weight: 500;
  padding: 0.6rem 1.2rem;
  border-radius: 8px;
  transition: all 0.2s ease;
  background-color: #0EA5E9;
  border-color: #0EA5E9;
  color: white;
}

.header-actions .btn:hover {
  background-color: #0284C7;
  border-color: #0284C7;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(14, 165, 233, 0.3);
}

.search-section {
  background: white;
  padding: 1.25rem;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(14, 165, 233, 0.05);
  border: 1px solid #E0F2FE;
  margin-bottom: 1.5rem !important;
}

.search-section .form-control,
.search-section .form-select {
  border-radius: 8px;
  border: 1px solid #BAE6FD;
  padding: 0.6rem 1rem;
  font-size: 0.95rem;
  transition: all 0.2s;
}

.search-section .form-control:focus,
.search-section .form-select:focus {
  border-color: #0EA5E9;
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.15);
}

.search-section .input-group-text {
  background-color: transparent;
  border-right: none;
  border-radius: 8px 0 0 8px;
  color: #7DD3FC;
}

.search-section .form-control {
  border-left: none;
}

.role-card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 6px -1px rgba(14, 165, 233, 0.05);
  border: 1px solid #E0F2FE;
  transition: transform 0.2s, box-shadow 0.2s;
}

.role-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 15px -3px rgba(14, 165, 233, 0.1);
}

.role-header {
  display: flex;
  align-items: flex-start;
  margin-bottom: 1.5rem;
}

.role-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #0EA5E9, #38BDF8);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  margin-right: 1rem;
  flex-shrink: 0;
  box-shadow: 0 4px 6px -1px rgba(14, 165, 233, 0.2);
}

.role-info {
  flex: 1;
}

.role-name {
  font-size: 1.1rem;
  font-weight: 600;
  color: #0C4A6E;
  margin-bottom: 0.25rem;
}

.role-description {
  font-size: 0.875rem;
  color: #475569;
  margin-bottom: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.badge {
  font-size: 0.75rem;
  padding: 0.4rem 0.8rem;
  border-radius: 6px;
  font-weight: 500;
  letter-spacing: 0.025em;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
}

.bg-success { background-color: #ECFDF5 !important; color: #059669 !important; }
.bg-danger { background-color: #FEF2F2 !important; color: #DC2626 !important; }

.role-stats {
  display: flex;
  background-color: #F8FAFC;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  border: 1px solid #F1F5F9;
}

.stat-item {
  flex: 1;
  text-align: center;
}

.stat-item:not(:last-child) {
  border-right: 1px solid #E2E8F0;
}

.stat-number {
  display: block;
  font-size: 1.25rem;
  font-weight: 600;
  color: #0EA5E9;
}

.stat-label {
  font-size: 0.75rem;
  color: #64748B;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.role-permissions {
  flex: 1;
  margin-bottom: 1.5rem;
}

.permissions-title {
  font-size: 0.95rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  color: #334155;
}

.permissions-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.permission-tag {
  background: #F0F9FF;
  color: #0284C7;
  padding: 0.25rem 0.6rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  border: 1px solid #BAE6FD;
}

.permission-more {
  background: #F1F5F9;
  color: #475569;
  padding: 0.25rem 0.6rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  border: 1px solid #E2E8F0;
}

/* 文字按钮样式 */
.role-actions {
  display: flex;
  gap: 0.5rem;
  border-top: 1px solid #F1F5F9;
  padding-top: 1.25rem;
}

.role-actions .btn {
  flex: 1;
  background: white;
  border: 1px solid #E2E8F0;
  padding: 0.4rem 0.5rem;
  font-size: 0.85rem;
  font-weight: 500;
  border-radius: 6px;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(14, 165, 233, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
}

.role-actions .btn i {
  display: none; /* 隐藏图标，只保留文字 */
}

.role-actions .btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(14, 165, 233, 0.08);
}

.role-actions .btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background-color: #F8FAFC;
}

.role-actions .btn-outline-primary { 
  color: #0284C7; 
  border-color: #BAE6FD;
  background-color: #F0F9FF;
}
.role-actions .btn-outline-primary:hover:not(:disabled) { 
  background-color: #E0F2FE;
  border-color: #38BDF8;
}

.role-actions .btn-outline-success { 
  color: #059669; 
  border-color: #A7F3D0;
  background-color: #ECFDF5;
}
.role-actions .btn-outline-success:hover:not(:disabled) { 
  background-color: #D1FAE5;
  border-color: #10B981;
}

.role-actions .btn-outline-warning { 
  color: #D97706; 
  border-color: #FDE68A;
  background-color: #FFFBEB;
}
.role-actions .btn-outline-warning:hover:not(:disabled) { 
  background-color: #FEF3C7;
  border-color: #F59E0B;
}

.role-actions .btn-outline-danger { 
  color: #DC2626; 
  border-color: #FECACA;
  background-color: #FEF2F2;
}
.role-actions .btn-outline-danger:hover:not(:disabled) { 
  background-color: #FEE2E2;
  border-color: #EF4444;
}

.permissions-config {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #CBD5E1;
  border-radius: 8px;
  padding: 1rem;
  background: #F8FAFC;
}

.form-check {
  margin-bottom: 0.75rem;
}

.form-check-label strong {
  color: #334155;
}

.modal.show {
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(4px);
}

.modal-content {
  border-radius: 16px;
  border: none;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.modal-header {
  border-bottom: 1px solid #E0F2FE;
  background-color: #F8FAFC;
  border-radius: 16px 16px 0 0;
  padding: 1.25rem 1.5rem;
}

.modal-title {
  color: #0C4A6E;
  font-weight: 600;
}

.modal-body {
  padding: 1.5rem;
}

.modal-footer {
  border-top: 1px solid #E0F2FE;
  padding: 1.25rem 1.5rem;
}

.modal-backdrop.show {
  opacity: 0;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .role-management {
    padding: 1rem;
  }
  
  .page-header {
    padding: 1.25rem;
  }
  
  .page-title {
    font-size: 1.5rem;
  }
  
  .search-section {
    padding: 1.25rem;
  }
  
  .role-card {
    padding: 1.25rem;
  }
  
  .role-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .role-icon {
    margin-right: 0;
    margin-bottom: 0.5rem;
  }
  
  .role-actions {
    flex-direction: column;
  }
  
  .role-actions .btn {
    flex: none;
    width: 100%;
  }
}

@media (max-width: 576px) {
  .role-stats {
    flex-direction: column;
    gap: 1rem;
  }
  
  .stat-item:not(:last-child) {
    border-right: none;
    border-bottom: 1px solid #E2E8F0;
    padding-bottom: 0.5rem;
  }
  
  .permissions-config {
    max-height: 200px;
  }
}
</style>